import {
  CSSProperties,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Link, useNavigate, useUrl} from '@shopify/hydrogen';

import {PlaceholderWhiskyJPEG} from '../assets/img';
import VectorIcon from './VectorIcon';
import QuantityAdjuster from './QuantityAdjuster.client';
import {useShopifyProductMetafields} from '../hooks';
import {formatAmount} from '../utils';

import classNames from 'classnames';
import {FlattenedProduct} from '../utils/graphql';
import {useMemberProduct} from '../contexts/MemberProductProvider.client';
import {AverageRatingType, MemberProductType} from '../types/backend';
import {useAuthService} from '../contexts/AuthServiceProvider.client';
import SwalComponent, {SwalLink} from './modals/SwalComponent.client';
import {
  getCheapestAvailableVariant,
  getIsTwlShopVariant,
  getIsVariantAvailableForSale,
} from '../utils/variants';
import ImageContainer from './ImageContainer.client';
import {REWARDS_IB_TAG} from '../constants';
import {calculateRewardsIbSaveAmount} from '../utils/rewards';
import {ProductVariant} from '@shopify/hydrogen/storefront-api-types';
import {TwlAddToCartButton} from './cart/TwlAddToCartButton.client';

interface ProductPreviewProps {
  product: FlattenedProduct;
  sampleProduct?: FlattenedProduct;
  openEditReviewModal: () => void;
  openMarketplacePricingModal: () => void;
  twlShopVariant?: ProductVariant;
  showcasedVariant?: ProductVariant;
  nonTwlPartnerVariants: ProductVariant[];
}

export default function ProductPreview({
  product,
  sampleProduct,
  openEditReviewModal,
  openMarketplacePricingModal,
  twlShopVariant,
  showcasedVariant,
  nonTwlPartnerVariants,
}: ProductPreviewProps): ReactElement {
  const [quantity, setQuantity] = useState(1);

  const {title, featuredImage, metafields, tags} = product;

  const {checkLoginRequired, authUser} = useAuthService();

  const isRewardsMember = useMemo(
    () => authUser?.member?.is_rewards_member ?? false,
    [authUser?.member?.is_rewards_member],
  );

  const saveAmount = useMemo(() => {
    if (!isRewardsMember && tags?.includes(REWARDS_IB_TAG)) {
      return calculateRewardsIbSaveAmount([
        ...nonTwlPartnerVariants,
        ...(twlShopVariant ? [twlShopVariant] : []),
      ]);
    }
    return null;
  }, [isRewardsMember, tags, twlShopVariant, nonTwlPartnerVariants]);

  const viableNonTwlPartnerVariants = useMemo(() => {
    return nonTwlPartnerVariants.filter(
      (variant) =>
        getIsVariantAvailableForSale(variant) &&
        showcasedVariant?.id !== variant.id,
    );
  }, [nonTwlPartnerVariants, showcasedVariant]);

  const comparisionPriceAmount = useMemo(() => {
    const totalVariants = viableNonTwlPartnerVariants.length;
    if (totalVariants === 0) return;
    const cheapestVariant = getCheapestAvailableVariant(
      viableNonTwlPartnerVariants,
    );
    if (cheapestVariant) {
      const cheapestVariantAmount = formatAmount(cheapestVariant.price.amount);
      return totalVariants > 1
        ? `${cheapestVariantAmount}+`
        : cheapestVariantAmount;
    }
    return undefined;
  }, [viableNonTwlPartnerVariants]);

  const {metafieldsKeyValue} = useShopifyProductMetafields(metafields);

  const {
    myMemberProduct,
    toggleWatchProduct,
    toggleCollectProduct,
    openRatingModal,
    setMyMemberProduct,
  } = useMemberProduct();

  const navigate = useNavigate();

  const {hash} = useUrl();
  useEffect(() => {
    if (hash === '#rewards') {
      // Scroll to the rewards section - we need to do this in a
      // useEffect, because hydrogen mucks up the position to scroll to
      // due to the header
      window.location.replace('#rewards-section');
    }
  }, [hash, navigate]);

  const sampleVariants = useMemo(
    () =>
      sampleProduct?.variants?.filter((variant) => variant.availableForSale) ??
      [],
    [sampleProduct],
  );

  const [selectedSampleVariant, setSelectedSampleVariant] =
    useState<ProductVariant | null>(() =>
      sampleVariants.length > 0 ? sampleVariants[0] : null,
    );

  const handleSampleVariantChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedVariantId = event.target.value;
      const selectedVariant = sampleVariants.find(
        (variant) => variant.id === selectedVariantId,
      );
      setSelectedSampleVariant(selectedVariant ?? null);
    },
    [sampleVariants],
  );

  const handleRatingModalSuccess = useCallback(
    (memberProduct: MemberProductType) => {
      setMyMemberProduct(memberProduct);
      SwalComponent.fire({
        customClass: {
          title: 'title-3 swal2-title-no-margin',
        },
        title: 'Thanks for your rating!',
        icon: 'success',
        html: (
          <>
            <p className="title-4 mb-10">Would you like to add a review?</p>
            <button
              type="button"
              className="button"
              onClick={() => {
                SwalComponent.close();
                openEditReviewModal();
              }}
            >
              Sure!
            </button>
            <button
              type="button"
              className="button button_color_gray_1 ml-10"
              onClick={() => SwalComponent.close()}
            >
              No Thanks
            </button>
            {!isRewardsMember && (
              <p className="title-4 mt-25 text-content">
                Earn 10 points for this rating,{' '}
                <SwalLink navigate={navigate} to="/l/rewards">
                  join Rewards
                </SwalLink>
              </p>
            )}
          </>
        ),
      });
    },
    [isRewardsMember, navigate, openEditReviewModal, setMyMemberProduct],
  );

  const rateWhiskyClickHandler = useCallback(
    () =>
      openRatingModal({
        productId: product.id,
        productImage: product?.featuredImage?.url,
        productTitle: product?.title,
        rating: myMemberProduct?.rating,
        onSuccess: handleRatingModalSuccess,
        skipSuccessModal: true,
      }),
    [
      handleRatingModalSuccess,
      myMemberProduct?.rating,
      openRatingModal,
      product?.featuredImage?.url,
      product.id,
      product?.title,
    ],
  );
  const isWatched = useMemo(
    () => myMemberProduct && myMemberProduct.is_watched,
    [myMemberProduct],
  );

  const isCollected = useMemo(
    () => myMemberProduct && myMemberProduct.is_added_to_collection,
    [myMemberProduct],
  );

  const isRated = useMemo(
    () =>
      myMemberProduct && !!myMemberProduct.rating && myMemberProduct.rating > 0,
    [myMemberProduct],
  );

  const isReviewed = useMemo(
    () => myMemberProduct && !!myMemberProduct.review_title,
    [myMemberProduct],
  );

  const viableAverageRating = useMemo((): AverageRatingType | null => {
    if (myMemberProduct && myMemberProduct.product.average_rating.value > 0) {
      return myMemberProduct.product.average_rating;
    } else if (
      metafieldsKeyValue?.averageRating &&
      metafieldsKeyValue.averageRating.value > 0
    ) {
      return metafieldsKeyValue.averageRating;
    }
  }, [myMemberProduct, metafieldsKeyValue]);

  const viableTotalReviews = useMemo((): number | null => {
    if (myMemberProduct && myMemberProduct.product.total_reviews > 0) {
      return myMemberProduct.product.total_reviews;
    } else if (metafieldsKeyValue && metafieldsKeyValue.totalReviews > 0) {
      return metafieldsKeyValue.totalReviews;
    }
  }, [myMemberProduct, metafieldsKeyValue]);

  const handleToggleCollectProductClick = useCallback(() => {
    // ShopifyProduct ID or BackendProduct.uuid
    const productId = myMemberProduct?.product
      ? myMemberProduct.product.uuid
      : product.id;
    toggleCollectProduct(isCollected, {
      product_id: productId,
    });
  }, [isCollected, product, myMemberProduct, toggleCollectProduct]);

  const twlShopVariantId = useMemo(() => {
    return twlShopVariant ? twlShopVariant.id : undefined;
  }, [twlShopVariant]);

  const getNonRewardsMemberPriceAdvert = useCallback((): ReactElement => {
    if (!twlShopVariantId) return <></>;

    let freeShippingBadgeDiv = (
      <div
        className="button button_size_small"
        style={{cursor: 'none', fontSize: '13px'}}
      >
        Rewards free shipping
      </div>
    );
    if (!isRewardsMember && twlShopVariantId) {
      freeShippingBadgeDiv = (
        <Link
          to="/l/rewards"
          className="button button_size_small"
          style={{fontSize: '13px'}}
        >
          Rewards free shipping
        </Link>
      );
    }
    return <p className="c-dark-gray mt-15">{freeShippingBadgeDiv}</p>;
  }, [isRewardsMember, twlShopVariantId]);

  const tileTextBadge = useMemo(() => {
    const tileTextBadgeValue = metafieldsKeyValue?.tileTextBadge?.trim();
    if (
      showcasedVariant &&
      getIsTwlShopVariant(showcasedVariant) &&
      tileTextBadgeValue &&
      tileTextBadgeValue.length > 0 &&
      tileTextBadgeValue !== 'null'
    ) {
      return tileTextBadgeValue;
    }
    return undefined;
  }, [metafieldsKeyValue?.tileTextBadge, showcasedVariant]);

  return (
    <section>
      <div className="product-page container pt-75">
        <div className="product-page__image product-page__image__container">
          <div className="image-gallery" id="links">
            {tileTextBadge && (
              <div className="product__ib-savings tag-element">
                {tileTextBadge}
              </div>
            )}
            <ImageContainer
              jpegSrc={
                featuredImage ? featuredImage.url : PlaceholderWhiskyJPEG
              }
              widthInPx={600}
              heightInPx={750}
              alt={title}
            />
          </div>
        </div>
        <div className="product-page__details">
          <h1 className="title-2">{title}</h1>
          <div className="product-page__review mt-20">
            {viableAverageRating ? (
              <div className="product-page__review__average">
                <span
                  className="reviews__item-content__rating rating-graph"
                  style={
                    {
                      '--rating': Math.round(viableAverageRating.value),
                    } as CSSProperties
                  }
                />
                <span className="product-page__review__average-text">
                  {viableAverageRating.value}&nbsp;({viableAverageRating.total}
                  &nbsp;
                  {`rating${viableAverageRating.total !== 1 ? 's' : ''}`})
                </span>
              </div>
            ) : (
              <div className="product-page__review__average-not">
                <i className="icon-heart-empty-2" />
                <span>
                  No Ratings yet -&nbsp;
                  <span
                    role="button"
                    tabIndex={-1}
                    style={{
                      textDecoration: 'underline',
                      color: '#f73400',
                      cursor: 'pointer',
                    }}
                    onClick={rateWhiskyClickHandler}
                  >
                    be the first
                  </span>
                </span>
              </div>
            )}
            {viableTotalReviews > 0 ? (
              <Link to="#product-reviews" className="product-page__review__all">
                See user reviews
              </Link>
            ) : (
              <div
                role="button"
                tabIndex={-1}
                className="product-page__review__all pointer"
                onClick={() => checkLoginRequired(openEditReviewModal)}
              >
                Add a review
              </div>
            )}
          </div>
          <div className="product-page__buttons">
            <button
              type="button"
              className={classNames('product-page__button', {
                active: isWatched,
              })}
              onClick={() =>
                toggleWatchProduct({
                  product: product.id,
                  is_watched: !isWatched,
                })
              }
            >
              <i className="icon-plus" />
              <span>Watch</span>
            </button>
            <button
              type="button"
              className={classNames('product-page__button', {
                active: isRated,
              })}
              onClick={rateWhiskyClickHandler}
            >
              <i className="icon-heart-empty-2" />
              <span>Rate</span>
            </button>
            <button
              type="button"
              className={classNames('product-page__button', {
                active: isReviewed,
              })}
              onClick={() => checkLoginRequired(openEditReviewModal)}
            >
              <i className="icon-review" />
              <span>Review</span>
            </button>
            <button
              type="button"
              className={classNames('product-page__button', {
                active: isCollected,
              })}
              onClick={handleToggleCollectProductClick}
            >
              <i className="icon-collection" />
              <span>Collection</span>
            </button>
          </div>
          <div className="product-page__details__image__container mt-30">
            {/* <div className="separator mt-30 mb-30" /> */}
            <div className="product-page__image">
              <div className="image-gallery" id="links">
                {tileTextBadge && (
                  <div className="product__ib-savings tag-element">
                    {tileTextBadge}
                  </div>
                )}
                <ImageContainer
                  jpegSrc={
                    featuredImage ? featuredImage.url : PlaceholderWhiskyJPEG
                  }
                  widthInPx={600}
                  heightInPx={750}
                  alt={title}
                />
              </div>
            </div>
          </div>
          {showcasedVariant ? (
            <>
              <div className="product-page__price">
                {viableNonTwlPartnerVariants.length == 0 ? (
                  <div
                    className="product-page__price-item best-price"
                    style={{width: '100%', border: 'none'}}
                  >
                    <div className="product-page__price-item-value price-item">
                      {formatAmount(showcasedVariant.price.amount)}
                    </div>
                    <div className="product-page__price-item-sub">
                      {showcasedVariant ? showcasedVariant.title : ''}
                      {getNonRewardsMemberPriceAdvert()}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="product-page__price-item best-price">
                      <div className="product-page__price-item-value price-item">
                        {formatAmount(showcasedVariant.price.amount)}
                      </div>
                      <div className="product-page__price-item-sub">
                        {showcasedVariant ? showcasedVariant.title : ''}
                        {getNonRewardsMemberPriceAdvert()}
                      </div>
                    </div>
                    <div className="product-page__price-item average-price">
                      <div className="product-page__price-item-value price-item">
                        {comparisionPriceAmount}
                      </div>
                      <div
                        role="button"
                        tabIndex={-1}
                        className="product-page__price-item-sub pointer"
                        onClick={openMarketplacePricingModal}
                      >
                        from {viableNonTwlPartnerVariants.length} other retailer
                        {viableNonTwlPartnerVariants.length > 1 ? 's' : ''}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="product-page__quantity">
                <div className="product-page__quantity-label"> Quantity </div>
                <QuantityAdjuster onChange={setQuantity} quantity={quantity} />
                <TwlAddToCartButton
                  variant={showcasedVariant}
                  quantity={quantity}
                  className="button button_size_large button-add-to-cart cart-preview-sidebar-js"
                  type="button"
                  title="Add to cart"
                  accessibleAddingToCartLabel="Adding item to your cart"
                >
                  Add to cart
                </TwlAddToCartButton>
              </div>
            </>
          ) : (
            <>
              <div className="product-page__price">
                <em>Currently Unavailable</em>
              </div>
              <div className="product-page__quantity">
                <div className="product-page__quantity-label"> Quantity </div>
                <QuantityAdjuster onChange={setQuantity} quantity={0} />
                <TwlAddToCartButton
                  variant={showcasedVariant}
                  quantity={quantity}
                  className="button button_size_large button-add-to-cart cart-preview-sidebar-js"
                  type="button"
                  title="Add to cart"
                  accessibleAddingToCartLabel="Adding item to your cart"
                  disabled
                >
                  Out of stock
                </TwlAddToCartButton>
              </div>
            </>
          )}
          <div className="separator mt-30 mb-30" />
          <div className="product-page__about">
            <h5 className="title-2">About this whisky</h5>
            <div className="product-page__meta">
              {metafieldsKeyValue.type && (
                <span className="meta-element">
                  <span className="meta-label">Type:</span>
                  <span className="meta-text">
                    <Link
                      to={`/shop/?types=${metafieldsKeyValue.type}`}
                      className="product-details__table__item__text"
                    >
                      {metafieldsKeyValue.type}
                    </Link>
                  </span>
                </span>
              )}
              {metafieldsKeyValue.country && (
                <span className="meta-element">
                  <span className="meta-label">Country:</span>
                  <span className="meta-text">
                    <VectorIcon
                      iconName={metafieldsKeyValue.country.iconName}
                    />
                    <Link
                      to={`/shop/?countries=${metafieldsKeyValue.country.value}`}
                      className="product-details__table__item__text"
                    >
                      {metafieldsKeyValue.country.label}
                    </Link>
                  </span>
                </span>
              )}
              {/* {metafieldsKeyValue.style && (
                <span className="meta-element">
                  <span className="meta-label">Style:</span>
                  <span className="meta-text">
                    <Link
                      to={`/shop/?styles=${metafieldsKeyValue.style}`}
                      className="product-details__table__item__text"
                    >
                      {metafieldsKeyValue.style}
                    </Link>
                  </span>
                </span>
              )} */}
            </div>
            <div className="product-page__about-text text-content c-gray">
              <div className="text-lg">{metafieldsKeyValue.summary}</div>
            </div>
            <a href="#product-details" className="product-page__about-more">
              View More Details <i className="icon-right-arrow1" />
            </a>
          </div>
          {saveAmount !== null && (
            <>
              <div className="separator mt-5 mb-30" />
              <div className="product-page__about" id="rewards-section">
                <h5 className="title-2">
                  Want to save {formatAmount(saveAmount)} on this whisky?
                </h5>
                <Link to="/l/rewards" className="product-page__about-more">
                  Join TWL Rewards & Save
                  <i className="icon-right-arrow1" />
                </Link>
              </div>
            </>
          )}
          {sampleVariants.length > 0 && (
            <>
              <div className="product-page__samples">
                <h5 className="title-2">
                  This Whisky Is Available As a Sample
                </h5>
                <div className="product-page__samples__details">
                  <div className="product-page__samples__details__size">
                    <span className="product-page__samples__details__label">
                      Size
                    </span>
                    <select
                      className="select select_style_line"
                      onChange={handleSampleVariantChange}
                    >
                      {!selectedSampleVariant && (
                        <option selected disabled>
                          Size
                        </option>
                      )}
                      {sampleVariants.map((variant) => (
                        <option key={variant.id} value={variant.id}>
                          {variant.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!!selectedSampleVariant?.price?.amount && (
                    <div className="product-page__samples__details__price">
                      <span className="product-page__samples__details__label">
                        Price
                      </span>
                      <span className="product-page__samples__details__price__amount">
                        {formatAmount(selectedSampleVariant.price.amount)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="product-page__samples__btn-container">
                  <TwlAddToCartButton
                    variant={selectedSampleVariant}
                    disabled={selectedSampleVariant == null}
                    quantity={1}
                    className="button button-add-to-cart"
                    type="button"
                  >
                    Add Sample to Cart
                  </TwlAddToCartButton>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
